import useWebSocketSubscription from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/hooks/useWebSocketSubscription";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { setUnreadChatCount, useUnreadChatCount } from "@/hooks/queries/lazo-chat-conversation/useLazoChatConversationQueries";
import {
  CLIENT_CONVERSATION_MESSAGES_QUERY_KEY,
  CLIENT_CONVERSATIONS_QUERY_KEY,
  KAM_CONVERSATION_MESSAGES_QUERY_KEY,
  KAM_CONVERSATIONS_QUERY_KEY,
  MESSAGING_QUERY_KEY,
  MESSAGING_UNREAD_COUNT_QUERY_KEY,
  useMessagingUnreadCount,
} from "@/hooks/queries/messaging/useMessagingQueries";
import { invalidateNotificationsCount, useNotificationsCount } from "@/hooks/queries/notification/useNotificationQueries";
import MessagingWebSocketClient from "@/utils/websocket/MessagingWebSocketClient";
import NotificationsWebSocketClient from "@/utils/websocket/NotificationsWebSocketClient";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext } from "react";

type NotificationsContextType = {
  amountUnread: number;
  amountChatUnread: number;
  updateAmount: () => void;
};

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotifications = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationsContextProvider");
  }

  return context;
};

export function NotificationsContextProvider({ children }) {
  const queryClient = useQueryClient();
  const { data: amountUnread, refetch: refetchAmountUnread } = useNotificationsCount();
  const { data: legacyChatUnread, refetch: refetchAmountChatUnread } = useUnreadChatCount();
  const { data: messagingUnread, refetch: refetchMessagingUnread } = useMessagingUnreadCount();

  // The navbar inbox badge combines the legacy chat unread count and the native messaging unread
  // count, since both feeds surface on the same Inbox button.
  const amountChatUnread = (legacyChatUnread ?? 0) + (messagingUnread ?? 0);

  function handleWsMessage(jsonMessage: string) {
    const message = JSON.parse(jsonMessage);

    switch (message.type) {
      case "NEW_NOTIFICATION":
        invalidateNotificationsCount();
        document.dispatchEvent(new CustomEvent("updateDueToNewNotificationReceived"));
        if (message.content.notification?.type === "DOCUMENT_PROCESS") {
          NotificationService.info(message.content.notification?.title ?? "You have a new notification");
        }
        return;
      case "NEW_MESSAGE":
        setUnreadChatCount(message.content.unreadChatMessages);
        NotificationService.info(`You received a new message from ${message.content.userName}`);
        document.dispatchEvent(new CustomEvent("updateDueToNewMessageReceived"));
        return;
      case "CONVERSATION_READ":
        setUnreadChatCount(message.content.unreadChatMessages);
        return;
    }
  }

  useWebSocketSubscription(NotificationsWebSocketClient, AuthService.getUser()?.externalId!, handleWsMessage);

  // Global owner of the native "messaging" channel. NotificationsContext is mounted for the whole app
  // lifetime, so a single subscription here drives every live messaging update (KAM and client inboxes
  // plus the navbar badge) without per-component subscriptions clobbering one another.
  const handleMessagingWsMessage = useCallback(
    (raw: string) => {
      let event: any;
      try {
        event = JSON.parse(raw);
      } catch {
        // Ignore malformed frames.
        return;
      }

      if (event?.type === "NEW_MESSAGE" && event.content) {
        const content = event.content;

        // Refresh every messaging surface so live inbound messages appear without a reload.
        queryClient.invalidateQueries({ queryKey: [KAM_CONVERSATIONS_QUERY_KEY] });
        if (content.conversation_id) {
          queryClient.invalidateQueries({ queryKey: [KAM_CONVERSATION_MESSAGES_QUERY_KEY, content.conversation_id] });
        }
        // Client messages are keyed by tenant; invalidate the prefix so all client-message queries refetch.
        queryClient.invalidateQueries({ queryKey: [MESSAGING_QUERY_KEY] });
        // Client multi-conversation inbox: refresh the list and the open thread live.
        queryClient.invalidateQueries({ queryKey: [CLIENT_CONVERSATIONS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [CLIENT_CONVERSATION_MESSAGES_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [MESSAGING_UNREAD_COUNT_QUERY_KEY] });

        const senderName = content.sender_name ?? content.senderName;
        if (senderName) {
          NotificationService.info(`You received a new message from ${senderName}`);
        }
      } else if (event?.type === "CONVERSATION_RESOLVED" && event.content) {
        const resolvedId = event.content.conversation_id as string | undefined;
        queryClient.invalidateQueries({ queryKey: [KAM_CONVERSATIONS_QUERY_KEY] });
        if (resolvedId) {
          queryClient.invalidateQueries({ queryKey: [KAM_CONVERSATION_MESSAGES_QUERY_KEY, resolvedId] });
        }
        // Also refresh the client thread so it shows the close divider live, plus the navbar badge.
        // Client messages are keyed by tenant; invalidate the prefix so all client-message queries refetch.
        queryClient.invalidateQueries({ queryKey: [MESSAGING_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [CLIENT_CONVERSATIONS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [CLIENT_CONVERSATION_MESSAGES_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [MESSAGING_UNREAD_COUNT_QUERY_KEY] });
      }
    },
    [queryClient]
  );

  useWebSocketSubscription(MessagingWebSocketClient, AuthService.getUser()?.externalId!, handleMessagingWsMessage, [
    handleMessagingWsMessage,
  ]);

  function updateAmount() {
    refetchAmountUnread();
    refetchAmountChatUnread();
    refetchMessagingUnread();
  }

  return <NotificationsContext.Provider value={{ amountUnread, amountChatUnread, updateAmount }}>{children}</NotificationsContext.Provider>;
}
